import { apiJobLevels } from "@/api/job-level.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiJobLevelBulkPayload, ApiJobLevelSavePayload } from "@/types/job-level.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const jobLevelsKeys = createQueryKeys("jobLevels", {
  list: (includeArchived: boolean) => ({
    queryKey: [includeArchived],
    queryFn: async () => {
      const result = await apiJobLevels.getAll(includeArchived);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? [];
    },
  }),
  byId: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiJobLevels.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useJobLevels = (includeArchived: boolean = false, enabled: boolean = true) => {
  return useQuery({
    ...jobLevelsKeys.list(includeArchived),
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useJobLevelById = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...jobLevelsKeys.byId(id),
    staleTime: 60 * 1000,
    enabled: enabled && id > 0,
  });
};

export interface JobLevelSaveParams {
  id: number;
  payload: ApiJobLevelSavePayload;
}

export const useJobLevelBulk = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiJobLevelBulkPayload) => {
      const result = await apiJobLevels.bulk(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? [];
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobLevelsKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobLevelsKeys.byId._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useJobLevelDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiJobLevels.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobLevelsKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobLevelsKeys.byId._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
